import { z } from 'zod';
/**
 * Structured output contracts for the image-post pipeline (spec §4, §11.2).
 * Every AI stage must return JSON matching these schemas; anything else is
 * a PROVIDER_OUTPUT error and the stage retries — output is never trusted
 * or executed.
 */
export const topicCandidatesSchema = z.object({
    topics: z
        .array(z.object({
        topic: z.string().min(3).max(300),
        category: z.string().max(80).optional(),
        audienceFit: z.number().min(0).max(100),
        rationale: z.string().max(600),
        sourceUrls: z.array(z.string().url()).default([]),
    }))
        .min(1)
        .max(20),
});
export const titleConceptSchema = z.object({
    title: z.string().min(3).max(300),
    concept: z.string().min(10).max(2000),
    visualPrompt: z.string().min(10).max(3000),
    negativePrompt: z.string().max(1000).default(''),
});
export const captionResultSchema = z.object({
    caption: z.string().min(5).max(5000),
    callToAction: z.string().max(300).default(''),
    hashtags: z.array(z.string().regex(/^#\S{1,60}$/)).max(15).default([]),
});
export const reviewResultSchema = z.object({
    overall: z.number().min(0).max(100),
    categories: z.object({
        topicRelevance: z.number().min(0).max(100),
        audienceFit: z.number().min(0).max(100),
        hookStrength: z.number().min(0).max(100),
        captionQuality: z.number().min(0).max(100),
        languageQuality: z.number().min(0).max(100),
        factualConfidence: z.number().min(0).max(100),
        originality: z.number().min(0).max(100),
        facebookSuitability: z.number().min(0).max(100),
        brandAlignment: z.number().min(0).max(100),
    }),
    explanation: z.string().max(3000),
    blockingIssues: z.array(z.string().max(500)).default([]),
    warnings: z.array(z.string().max(500)).default([]),
    recommendedFixes: z.array(z.string().max(500)).default([]),
});
/** Per-page research configuration stored in page_profiles.researchConfig. */
export const researchConfigSchema = z.object({
    feeds: z.array(z.string().url()).default([]),
    maxSourceAgeDays: z.number().min(0.25).max(90).default(7),
    minSourceCount: z.number().min(0).max(10).default(1),
    rejectUndated: z.boolean().default(true),
    topicsPerRun: z.number().min(1).max(20).default(6),
    duplicateTopicCooldownDays: z.number().min(1).max(365).default(30),
    // Per-page image-post style references. Asset IDs keep the JSON config
    // migration-free while the files remain in the managed media library.
    imageReferenceAssetIds: z.array(z.string().uuid()).max(5).default([]),
    // Composite the Page's own brand mark (cropped from the first reference's
    // upper-right corner) onto finished posts. Opt-out for Pages whose reference
    // layout doesn't carry a top-right logo. Defaults on for backward behavior.
    brandMarkFromReference: z.boolean().default(true),
});
//# sourceMappingURL=pipeline.js.map